import styled from 'styled-components';

import { Button, Text } from '../../../../components';
import { ANIM, COLORS, MOBILE, OWN_LIST, SORTABLE_ANIM, SPACE } from '@/nova/theme/tokens';

/* The page, its lists and how many are hidden, then how to reorder and when the site sees it. */
export const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/* The lists to reorder, one row each. The drag measures the list's children as the rows, so nothing else
   goes directly in it. */
export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/* One list. While a row is dragged the rows move by an inline transform (it changes on every pointer move);
   an idle row carries none. A list the page hides is outlined dashed, as its chip is ($hidden). */
export const Row = styled.li<{ $shift: number; $dragged: boolean; $animated: boolean; $hidden?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[12]} ${SPACE[16]} ${SPACE[12]} ${SPACE[8]};
  border-radius: 14px;
  border: 1px ${({ $hidden }) => ($hidden ? 'dashed' : 'solid')}
    ${({ $dragged }) => ($dragged ? COLORS.ctaBorder : COLORS.hairline)};
  background: ${({ $dragged }) => ($dragged ? COLORS.surfaceCard : COLORS.surfaceFaint)};
  box-shadow: ${({ $dragged }) => ($dragged ? COLORS.menuShadow : 'none')};
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  overflow-anchor: none;
  transform: ${({ $shift }) => ($shift !== 0 ? `translateY(${$shift}px)` : 'none')};
  transition: ${({ $animated, $dragged }) =>
    $animated
      ? `transform ${$dragged ? SORTABLE_ANIM.SETTLE_MS : SORTABLE_ANIM.SHIFT_MS}ms ${SORTABLE_ANIM.EASE},
         box-shadow ${SORTABLE_ANIM.SETTLE_MS}ms ${SORTABLE_ANIM.EASE}`
      : 'none'};

  ${({ $dragged }) => ($dragged ? 'z-index: 2;' : '')}

  &:focus-within {
    z-index: 3;
  }

  /* A phone has no room for the row on one line: the name keeps the first line, the buttons share a second one, on
     the right. */
  ${MOBILE} {
    flex-wrap: wrap;
    justify-content: flex-end;
    row-gap: ${SPACE[8]};
  }
`;

/* The drag handle: also a button, so Arrow Up / Down move the row from the keyboard. */
export const Handle = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  width: 28px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: grab;
  touch-action: none;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    color: ${COLORS.text};
  }

  &:active {
    cursor: grabbing;
  }
`;

/* The list's place in the order (1-based). */
/* A hidden list keeps its row and its place; its number and name go muted. */
export const Position = styled.span<{ $muted?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 ${SPACE[8]};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $muted }) => ($muted ? COLORS.textMuted : COLORS.heading)};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

/* The mark of an own list, as its row carries it on the Games tab (ListRow): a lilac disc with the pencil in
   the page's colour, or a dark disc with a lilac pencil when the list is hidden. The pencil takes the disc's
   text colour (the icon draws in currentColor). */
export const OwnMark = styled.span<{ $hidden: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $hidden }) => ($hidden ? OWN_LIST.hiddenDisc : OWN_LIST.mark)};
  color: ${({ $hidden }) => ($hidden ? OWN_LIST.hiddenPencil : COLORS.pageBg)};
`;

export const Titles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};

  /* Wide enough that nothing after it fits on the first line (the handle, the number and the disc take the
     rest), so the buttons wrap to the second. */
  ${MOBILE} {
    flex-basis: calc(100% - 120px);
  }
`;

export const RowTitle = styled(Text).attrs({ tag: 'span' as const })<{ $muted?: boolean }>`
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $muted }) => ($muted ? COLORS.textMuted : COLORS.heading)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled(Text).attrs({ tag: 'span' as const })`
  overflow: hidden;
  font-size: 12px;
  color: ${COLORS.textMuted};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* "Hidden on your site", for screen readers only: the row's dashed border says it to sighted owners, as on the chips
   (the page keeps the list in its place but does not show it). */
export const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

/* A row's own actions, at its end: Edit (the owner's own lists) and Hide / Show. Quiet, so the row still
   reads as the list, except Show: the way back for a list that is off the site takes the outline Button's
   look (same tokens). */
export const RowBtn = styled.button<{ $show?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  box-sizing: border-box;
  height: 30px;
  padding: 0 ${SPACE[12]};
  border: 1px solid ${({ $show }) => ($show ? COLORS.ctaBorder : COLORS.hairline)};
  border-radius: 999px;
  background: ${({ $show }) => ($show ? COLORS.ctaOutlineBg : 'transparent')};
  color: ${({ $show }) => ($show ? COLORS.ctaText : COLORS.textMuted)};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled),
  &:focus-visible {
    color: ${({ $show }) => ($show ? COLORS.ctaText : COLORS.text)};
    border-color: ${COLORS.ctaBorder};
  }

  /* Edit while one of the owner's lists is being stored: faded, as the arrows are at their end of the list. */
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  /* On a phone the glyph alone; the button's label (aria-label) still names it. */
  ${MOBILE} {
    padding: 0 ${SPACE[8]};
  }
`;

export const RowBtnLabel = styled(Text).attrs({ tag: 'span' as const })`
  ${MOBILE} {
    display: none;
  }
`;

/* Move up / Move down: round, in the row buttons' quiet look, faded at their end of the list (`aria-disabled`, so
   the keyboard focus stays on them there). */
export const ArrowBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid ${COLORS.hairline};
  border-radius: 999px;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not([aria-disabled='true']),
  &:focus-visible {
    color: ${COLORS.text};
    border-color: ${COLORS.ctaBorder};
  }

  &[aria-disabled='true'] {
    opacity: 0.35;
    cursor: default;
  }
`;

/* The footer: the status on the left (the copy's unpublished change, or the wait for a running publish), Cancel and
   Save order on the right. On a phone a status with words takes a line of its own above the buttons. */
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;

  ${MOBILE} {
    flex-wrap: wrap;
    row-gap: ${SPACE[8]};
  }
`;

/* Always mounted, as a live region must be, and empty when there is nothing to say: then it takes no line. */
export const Status = styled.span`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-size: 13px;
  color: ${COLORS.textMuted};

  ${MOBILE} {
    &:not(:empty) {
      flex: 1 1 100%;
    }
  }
`;

/* Publish blue, as the tab's unpublished marks. */
export const StatusDot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${COLORS.publishBlue};
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  margin-left: auto;
`;

/* Save order reads as unavailable while a publish runs but is NOT `disabled`: its handler refuses instead, as
   the list editor's confirm does, and the footer's status says why. */
export const PrimaryBtn = styled(Button)<{ $off: boolean }>`
  opacity: ${({ $off }) => ($off ? 0.45 : 1)};
  white-space: nowrap;
`;
